import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, assetUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, Leaf, GraduationCap, PawPrint, Globe2, HandHeart, Sparkles, Video, Loader2, Send } from "lucide-react";

const PILLARS = [
  { icon: HandHeart, title: "Local Business First", text: "Every purchase supports a verified Jamaican merchant. We take low fees so more of your money stays with local families and makers." },
  { icon: GraduationCap, title: "Youth & Skills", text: "We fund digital-skills workshops and internships so young Jamaicans can build careers in tech, logistics and e-commerce." },
  { icon: Leaf, title: "Greener Deliveries", text: "Consolidated island routes and reusable packaging cut waste and emissions across every SHOP NOW delivery." },
  { icon: PawPrint, title: "Animal Welfare", text: "A share of our new Pets & Animals category funds shelters and spay/neuter drives across the Caribbean." },
];

const IMPACT = [
  { n: "500+", l: "Local merchants onboarded" },
  { n: "14", l: "Parishes served" },
  { n: "J$12M+", l: "Paid to Jamaican sellers" },
  { n: "20+", l: "Community drives supported" },
];

const IMAGES = [
  "https://images.unsplash.com/photo-1581536678606-3a35fecc8fc5?w=800",
  "https://images.unsplash.com/photo-1777309816812-89c4d50b878d?w=800",
  "https://images.unsplash.com/photo-1518302779017-75fa8bb95730?w=800",
];

function CommunityWall() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [caption, setCaption] = useState("");
  const [media, setMedia] = useState({ url: "", is_video: false });
  const [upBusy, setUpBusy] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/community/posts").then(({ data }) => setPosts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!user) { toast.error("Please sign in to share"); return; }
    setUpBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMedia({ url: data.url, is_video: data.is_video });
      toast.success(data.is_video ? "Video attached" : "Photo attached");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed (video max 50MB)"); }
    setUpBusy(false);
  };

  const post = async () => {
    if (!user) { toast.error("Please sign in to share"); return; }
    if (!caption.trim() && !media.url) { toast.error("Add a caption or media"); return; }
    setBusy(true);
    try {
      await api.post("/community/posts", { caption, media_url: media.url, is_video: media.is_video });
      toast.success("Shared with the community!");
      setCaption(""); setMedia({ url: "", is_video: false });
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not post"); }
    setBusy(false);
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="community-wall">
      <h2 className="font-display text-2xl font-bold tracking-tight mb-5 flex items-center gap-2"><Video size={20} className="text-emerald" /> Community Wall</h2>
      <div className="bg-card rounded-2xl border border-border p-5 mb-5">
        <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={2} placeholder="Share a story, a win, or a shout-out to a local maker…" className="w-full rounded-xl border border-border p-3 text-sm" data-testid="wall-caption" />
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <label className="flex items-center gap-2 text-xs font-semibold text-ink cursor-pointer bg-white border border-border rounded-xl px-3 py-2 hover:bg-secondary" data-testid="wall-media-label">
            {upBusy ? <Loader2 size={14} className="animate-spin" /> : <Video size={14} className="text-emerald" />}
            {media.url ? "Media attached ✓" : "Add photo / video"}
            <input type="file" accept="image/*,video/*" onChange={upload} className="hidden" data-testid="wall-media-input" />
          </label>
          {media.url && <button onClick={() => setMedia({ url: "", is_video: false })} className="text-xs text-coral font-semibold">Remove</button>}
          <button onClick={post} disabled={busy} className="ml-auto flex items-center gap-2 bg-ink text-white rounded-xl px-5 py-2.5 text-sm font-semibold disabled:opacity-60" data-testid="wall-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Share
          </button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wall-posts">
        {posts.length === 0 ? <p className="text-sm text-muted-foreground">Be the first to share something with the community.</p> : posts.map((p) => (
          <div key={p.id} className="bg-card rounded-2xl border border-border overflow-hidden" data-testid={`wall-post-${p.id}`}>
            {p.media_url && (p.is_video
              ? <video src={assetUrl(p.media_url) + "#t=0.5"} preload="metadata" controls playsInline className="w-full h-48 object-cover bg-ink" />
              : <img src={assetUrl(p.media_url)} alt="" className="w-full h-48 object-cover" />)}
            <div className="p-4">
              <p className="text-sm text-slate-700">{p.caption}</p>
              <div className="text-xs text-muted-foreground mt-2 font-semibold">{p.author}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Community() {
  return (
    <div className="bg-background" data-testid="community-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
        <div className="rounded-3xl sn-ink-bg sn-noise relative overflow-hidden p-8 sm:p-14 text-white">
          <div className="relative max-w-2xl">
            <span className="inline-flex items-center gap-2 sn-glass sn-pill px-4 py-1.5 text-xs font-semibold uppercase tracking-wider"><Globe2 size={14} /> Social Responsibility</span>
            <h1 className="font-clash font-bold text-4xl sm:text-5xl mt-4 leading-tight">SHOP NOW gives back —<br />from Jamaica to the world.</h1>
            <p className="text-white/75 mt-4 text-base">We're more than a marketplace. We reinvest in the communities that power us — supporting local business, youth, the environment and animal welfare across Jamaica, the Caribbean and beyond.</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/jamaica-made" className="bg-coral text-white rounded-xl px-6 py-3 font-semibold hover:bg-white hover:text-ink transition-colors" data-testid="community-shop-local">Shop Jamaica-Made</Link>
              <Link to="/careers" className="sn-glass rounded-xl px-6 py-3 font-semibold hover:bg-white/20 transition-colors" data-testid="community-careers">Join our mission</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="community-impact">
          {IMPACT.map((s) => (
            <div key={s.l} className="bg-card rounded-2xl border border-border p-6 text-center">
              <div className="font-clash font-bold text-3xl text-emerald">{s.n}</div>
              <div className="text-sm text-muted-foreground mt-1">{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <h2 className="font-display text-2xl font-bold tracking-tight mb-5 flex items-center gap-2"><Heart size={20} className="text-coral" /> How we care</h2>
        <div className="grid md:grid-cols-2 gap-5">
          {PILLARS.map((p) => (
            <div key={p.title} className="bg-card rounded-2xl border border-border p-6 flex gap-4 sn-card-hover" data-testid={`community-pillar-${p.title.split(" ")[0].toLowerCase()}`}>
              <span className="h-12 w-12 rounded-xl bg-emerald/10 text-emerald grid place-items-center shrink-0"><p.icon size={24} /></span>
              <div>
                <h3 className="font-display font-bold text-lg">{p.title}</h3>
                <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{p.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid sm:grid-cols-3 gap-4">
          {IMAGES.map((src, i) => (
            <img key={src} src={src} alt="SHOP NOW community" className="rounded-2xl h-56 w-full object-cover" data-testid={`community-image-${i}`} />
          ))}
        </div>
      </section>

      <CommunityWall />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="rounded-3xl bg-emerald text-white sn-noise relative overflow-hidden p-8 sm:p-12 text-center">
          <div className="relative max-w-2xl mx-auto">
            <Sparkles size={28} className="mx-auto mb-3" />
            <h2 className="font-clash font-bold text-3xl">Partner with SHOP NOW for good.</h2>
            <p className="text-white/80 mt-3">Are you a Jamaican or Caribbean non-profit, shelter or community group? Let's work together — from sponsorships to donation drives.</p>
            <Link to="/support" className="inline-block mt-6 bg-white text-emerald-deep rounded-full px-8 py-3 font-semibold hover:bg-ink hover:text-white transition-colors" data-testid="community-partner-btn">Get in touch</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
